import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CLIENT_ICON_EVENT, iconSrcFromSplashRecord, setClientIconUrl } from "../utils/clientIcon";
import { apiFetch, useAuth } from "../context/AuthContext";
import { useNotifications } from "../context/NotificationContext";
import { LanguageContext } from "../constant/LanguageContext";
// Bundled by webpack (hashed into the build), so it ships with every deploy —
// no storage URL to expire.
import nexployIcon from "../Images/photos/icon_nexploy_with_bg.png";

// A public/marketing/login route has no logged-in company context, so a
// company badge makes no sense there (mirrors MiniChatDock's own exclusion
// list for the same routes).
const HIDDEN_ON_ROUTES = ["/", "/login", "/reset-password", "/privacy", "/delete-account"];

const CIRCLE_SIZE = 44;
// Distance of the rope's top anchor, and the badge's resting spot, from the
// trailing edge — right in LTR, left in RTL so it stays clear of the sidebar
// (which moves to the right in Arabic).
const SIDE_INSET = 34;
const DEFAULT_TOP = 96; // resting distance from the top — clears typical header content
const EDGE_MARGIN = CIRCLE_SIZE / 2 + 8; // keeps the badge fully on-screen while actively being dragged
const SPRING_BACK_MS = 650;

const BASE_URL = process.env.REACT_APP_BASE_URL;

// The stored icon src is a signed storage URL that expires an hour after it
// was issued, so the copy saved at login goes stale in a long session or
// after a reload. Re-pull the record to get a fresh one. Returns false only
// when the refresh itself couldn't run or failed.
const refreshClientIcon = async () => {
  const clientName = localStorage.getItem("clientName");
  if (!clientName || !localStorage.getItem("token")) return false;
  try {
    const res = await apiFetch(`${BASE_URL}/api/client-splash/${encodeURIComponent(clientName)}`);
    if (res.status === 404) {
      setClientIconUrl(null);
      return true;
    }
    if (!res.ok) return false;
    const json = await res.json();
    setClientIconUrl(iconSrcFromSplashRecord(json?.data));
    return true;
  } catch {
    return false;
  }
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Overshoots slightly past the resting point before settling — reads as an
// elastic rope snapping back rather than a flat linear slide.
const easeOutBack = (t) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

const rootStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 9998,
  pointerEvents: "none",
};

const svgStyle = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
};

const circleWrapperStyle = {
  position: "absolute",
  width: CIRCLE_SIZE,
  height: CIRCLE_SIZE,
  borderRadius: "50%",
  pointerEvents: "auto",
  touchAction: "none",
  transformOrigin: "50% -6px",
  userSelect: "none",
  WebkitUserSelect: "none",
};

const circleFrameStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  overflow: "hidden",
  background: "#ffffff",
  border: "1.5px solid rgba(255, 255, 255, 0.92)",
  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.28), 0 0 0 1px rgba(15, 23, 42, 0.06)",
};

const circleImageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
};

const swingStyleTag = `
@keyframes clientBadgeRopeSwing {
  0%   { transform: rotate(0deg); }
  22%  { transform: rotate(15deg); }
  44%  { transform: rotate(-10deg); }
  64%  { transform: rotate(6deg); }
  82%  { transform: rotate(-3deg); }
  100% { transform: rotate(0deg); }
}
.client-badge-rope-swing {
  animation: clientBadgeRopeSwing 0.85s cubic-bezier(0.36, 0.07, 0.19, 0.97);
}
`;

export default function ClientBadge() {
  const location = useLocation();
  const { clientBadgeDisabled } = useNotifications();
  const { user } = useAuth();
  // Superadmins belong to no client — their badge is always the Nexploy mark.
  const isSuperAdmin = String(user?.role || "").toLowerCase() === "superadmin";
  const { language } = useContext(LanguageContext);
  const isRTL = language === "ar";
  const [iconUrl, setIconUrl] = useState(() => localStorage.getItem("clientIconUrl"));
  const [hasError, setHasError] = useState(false);
  // One refresh per failure streak, so a URL that is still broken after a
  // refresh hides the badge instead of looping.
  const refreshTriedRef = useRef(false);
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 1280,
    height: typeof window !== "undefined" ? window.innerHeight : 800,
  }));
  const [isDragging, setIsDragging] = useState(false);
  const [swingKey, setSwingKey] = useState(0);

  const anchorX = isRTL ? SIDE_INSET : viewport.width - SIDE_INSET;

  // The one true resting spot — top trailing corner (top-right, or top-left
  // in RTL), below the header. The badge always returns here after a drag;
  // it never remembers a dropped position.
  const restingPos = useCallback(
    (vw) => ({ x: isRTL ? SIDE_INSET : vw - SIDE_INSET, y: DEFAULT_TOP }),
    [isRTL]
  );

  const clampToViewport = useCallback(
    (x, y, vw, vh) => ({
      x: clamp(x, EDGE_MARGIN, vw - EDGE_MARGIN),
      y: clamp(y, EDGE_MARGIN, vh - EDGE_MARGIN),
    }),
    []
  );

  const [pos, setPos] = useState(() => restingPos(viewport.width));
  const posRef = useRef(pos);
  posRef.current = pos;

  const dragRef = useRef({ startX: 0, startY: 0, originX: 0, originY: 0, active: false, moved: false });
  const springFrameRef = useRef(null);

  const cancelSpring = useCallback(() => {
    if (springFrameRef.current) {
      cancelAnimationFrame(springFrameRef.current);
      springFrameRef.current = null;
    }
  }, []);

  // Animates the badge (and, since the rope's SVG path is derived from the
  // same `pos` state every frame, the rope right along with it) from wherever
  // it was dropped back to its one fixed resting spot — like letting go of a
  // pulled rope and watching it swing back to hanging straight.
  const springBackToRest = useCallback(() => {
    cancelSpring();
    const from = { ...posRef.current };
    const to = restingPos(window.innerWidth);
    const start = performance.now();

    const step = (now) => {
      const t = Math.min(1, (now - start) / SPRING_BACK_MS);
      const eased = easeOutBack(t);
      setPos({
        x: from.x + (to.x - from.x) * eased,
        y: from.y + (to.y - from.y) * eased,
      });
      if (t < 1) {
        springFrameRef.current = requestAnimationFrame(step);
      } else {
        setPos(to);
        springFrameRef.current = null;
      }
    };
    springFrameRef.current = requestAnimationFrame(step);
  }, [cancelSpring, restingPos]);

  // On resize, snap the resting spot to the new viewport (unless a drag or a
  // spring-back is actively in flight — leave those alone, they'll land on
  // the up-to-date resting spot on their own).
  useEffect(() => {
    const handleResize = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      setViewport({ width: vw, height: vh });
      if (!dragRef.current.active && !springFrameRef.current) {
        setPos(restingPos(vw));
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [restingPos]);

  // Language switch flips the side — jump straight to the new resting spot.
  useEffect(() => {
    cancelSpring();
    setPos(restingPos(window.innerWidth));
  }, [restingPos, cancelSpring]);

  useEffect(() => {
    const syncFromStorage = (event) => {
      if (!event || event.key === "clientIconUrl") {
        setHasError(false);
        setIconUrl(localStorage.getItem("clientIconUrl"));
      }
    };

    const handleCustom = (event) => {
      setHasError(false);
      if (event?.detail && Object.prototype.hasOwnProperty.call(event.detail, "iconUrl")) {
        setIconUrl(event.detail.iconUrl || null);
      } else {
        setIconUrl(localStorage.getItem("clientIconUrl"));
      }
    };

    window.addEventListener("storage", syncFromStorage);
    window.addEventListener(CLIENT_ICON_EVENT, handleCustom);

    // The stored URL may already be expired (e.g. after a page reload).
    if (localStorage.getItem("clientIconUrl")) refreshClientIcon();

    return () => {
      window.removeEventListener("storage", syncFromStorage);
      window.removeEventListener(CLIENT_ICON_EVENT, handleCustom);
    };
  }, []);

  useEffect(() => {
    if (!iconUrl) setHasError(false);
  }, [iconUrl]);

  useEffect(() => () => cancelSpring(), [cancelSpring]);

  const triggerSwing = useCallback(() => {
    setSwingKey((k) => k + 1);
  }, []);

  const handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    cancelSpring(); // grabbing it mid-spring-back takes over from its current position
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* ignore */ }
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: posRef.current.x,
      originY: posRef.current.y,
      active: true,
      moved: false,
    };
    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current.active) return;
    const dx = event.clientX - dragRef.current.startX;
    const dy = event.clientY - dragRef.current.startY;
    if (!dragRef.current.moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
      dragRef.current.moved = true;
    }
    if (!dragRef.current.moved) return;
    event.preventDefault();
    const next = clampToViewport(
      dragRef.current.originX + dx,
      dragRef.current.originY + dy,
      viewport.width,
      viewport.height
    );
    setPos(next);
  };

  const endDrag = (event) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    setIsDragging(false);
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* ignore */ }

    if (dragRef.current.moved) {
      springBackToRest();
      return;
    }

    triggerSwing();
  };

  const badgeSrc = isSuperAdmin ? nexployIcon : iconUrl;

  if (
    !badgeSrc ||
    hasError ||
    clientBadgeDisabled ||
    HIDDEN_ON_ROUTES.includes(location.pathname)
  ) {
    return null;
  }

  const circleTopY = pos.y - CIRCLE_SIZE / 2;
  const controlY = circleTopY * 0.55;
  const controlX = (anchorX + pos.x) / 2;
  const ropePath = `M ${anchorX} 0 Q ${controlX} ${controlY} ${pos.x} ${circleTopY}`;

  return (
    <div style={rootStyle}>
      <style>{swingStyleTag}</style>
      <svg style={svgStyle} width={viewport.width} height={viewport.height} aria-hidden="true">
        <path d={ropePath} fill="none" stroke="rgba(100, 112, 130, 0.55)" strokeWidth="1.5" />
      </svg>
      <div
        key={swingKey}
        className={swingKey ? "client-badge-rope-swing" : undefined}
        style={{
          ...circleWrapperStyle,
          left: pos.x - CIRCLE_SIZE / 2,
          top: circleTopY,
          cursor: isDragging ? "grabbing" : "grab",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div style={circleFrameStyle}>
          <img
            src={badgeSrc}
            alt={isSuperAdmin ? "Nexploy" : "Company"}
            style={circleImageStyle}
            draggable={false}
            loading="lazy"
            onLoad={() => {
              refreshTriedRef.current = false;
            }}
            onError={async () => {
              // The bundled Nexploy icon has no signed URL to refresh.
              if (isSuperAdmin || refreshTriedRef.current) {
                setHasError(true);
                return;
              }
              refreshTriedRef.current = true;
              const failedSrc = iconUrl;
              const refreshed = await refreshClientIcon();
              // Same src back (e.g. a dead external link) won't re-render the
              // <img>, so no second error would fire — hide it now.
              if (!refreshed || localStorage.getItem("clientIconUrl") === failedSrc) {
                setHasError(true);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
